/**
 * Which wall cards to mount (Part 04 Task 3): the cards in the viewport, by
 * binary search per column, plus overscan within a per-column budget of 8
 * (24/32/40 at 3–5 columns).
 */
import type { WallColumn, WallLayout } from "./wall-layout";

const MOUNT_BUDGET_PER_COLUMN = 8;

/** Extra height mounted above and below the viewport. */
export function wallOverscan(columns: number, viewportHeight: number): number {
  return viewportHeight * (columns <= 4 ? 0.5 : 0.25);
}

function firstCardEndingAfter(column: WallColumn, y: number): number {
  let low = 0;
  let high = column.ids.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    if (column.tops[middle]! + column.heights[middle]! > y) high = middle;
    else low = middle + 1;
  }
  return low;
}

/** Cards that intersect `[top, bottom)`, by binary search per column. */
export function visibleCards(
  layout: WallLayout,
  top: number,
  bottom: number,
): string[] {
  const ids: string[] = [];
  for (const column of layout.columns) {
    for (
      let card = firstCardEndingAfter(column, top);
      card < column.ids.length && column.tops[card]! < bottom;
      card += 1
    ) {
      ids.push(column.ids[card]!);
    }
  }
  return ids;
}

/**
 * The cards in `[top, bottom)`, then those within one more view below
 * that are not in it yet: work done for them happens before they show.
 */
export function cardsNearView(
  layout: WallLayout,
  top: number,
  bottom: number,
): string[] {
  const inView = visibleCards(layout, top, bottom);
  const shown = new Set(inView);
  const below = visibleCards(layout, bottom, 2 * bottom - top).filter(
    (id) => !shown.has(id),
  );
  return [...inView, ...below];
}

/**
 * The cards to mount: every card in the viewport, then overscan cards
 * nearest first while each column stays within its budget. Reading order
 * (top to bottom, then left to right), so focus order follows the wall.
 */
export function mountedCards(
  layout: WallLayout,
  viewTop: number,
  viewBottom: number,
  overscan: number,
): string[] {
  const columns = layout.geometry.columns;
  const mounted: string[] = [];
  const counts = new Array<number>(columns).fill(0);
  const nearby: { id: string; distance: number }[][] = Array.from(
    { length: columns },
    () => [],
  );
  for (const id of visibleCards(
    layout,
    viewTop - overscan,
    viewBottom + overscan,
  )) {
    const box = layout.boxes.get(id)!;
    if (box.y + box.height > viewTop && box.y < viewBottom) {
      mounted.push(id);
      counts[box.column]! += 1;
      continue;
    }
    const distance =
      box.y >= viewBottom ? box.y - viewBottom : viewTop - (box.y + box.height);
    nearby[box.column]!.push({ id, distance });
  }
  nearby.forEach((candidates, column) => {
    candidates.sort((left, right) => left.distance - right.distance);
    for (const candidate of candidates) {
      if (counts[column]! >= MOUNT_BUDGET_PER_COLUMN) break;
      mounted.push(candidate.id);
      counts[column]! += 1;
    }
  });
  return mounted.sort((left, right) => {
    const a = layout.boxes.get(left)!;
    const b = layout.boxes.get(right)!;
    return a.y - b.y || a.x - b.x;
  });
}
